import {
  Autocomplete,
  Button,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Select,
  TextField
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImagePaths } from '../../../Utility/ImagePaths';
import RestAPIService from '../../../Utility/Services/RestAPIService';
import { Serviceurls } from '../../../Utility/API/Serviceurls';
import CommonValidation from '../../../Components/CommonFunctions/CommonValidation';
import { localGetItem } from '../../../Utility/Services/CacheProviderService';
import { CurrencySymbol } from '../../../Utility/Constants';
import ToastMsg from '../../../Components/ToastMsg/ToastMsg';
import '../PharmacySettings/PharmaSettings.css';
import '../../Laboratory/Inventory/inventory.css';

const ExpiryAlertList = [
  { value: 1, label: '1 Month' },
  { value: 2, label: '2 Months' },
  { value: 3, label: '3 Months' },
  { value: 6, label: '6 Months' }
];

const parseExpiryParts = (dateString) => {
  if (!dateString) return null;
  if (/^\d{2}-\d{4}$/.test(dateString)) {
    const [month, year] = dateString.split('-');
    return { month: parseInt(month, 10), year: parseInt(year, 10) };
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month] = dateString.split('-');
    return { month: parseInt(month, 10), year: parseInt(year, 10) };
  }
  return null;
};

const formatExpiryDateToDateValue = (expiryDateString) => {
  if (!expiryDateString) return null;
  try {
    const dateParts = parseExpiryParts(expiryDateString);
    if (!dateParts) return null;
    const { month, year } = dateParts;
    if (month >= 1 && month <= 12 && year > 1900) {
      return new Date(year, month, 0);
    }
  } catch (e) {
    /* ignore */
  }
  return null;
};

const formatDateYmd = (date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const getLastDateOfMonth = (dateString) => {
  try {
    if (!dateString) return null;
    const dateParts = parseExpiryParts(dateString);
    if (!dateParts) return null;
    const { month, year } = dateParts;
    if (month < 1 || month > 12) return null;
    const lastDateOfMonth = new Date(year, month, 0);
    return formatDateYmd(lastDateOfMonth);
  } catch (e) {
    return null;
  }
};

const dosageTypeToString = (item) => {
  if (item == null || item === '') return '';
  if (typeof item === 'string') return item;
  return (
    item.dosage_type ||
    item.dosagetype_name ||
    item.name ||
    String(item.dosagetype_id ?? '')
  );
};

const emptyForm = () => ({
  stock_id: null,
  drug_name: '',
  generic_name: '',
  generic_id: null,
  drug_strength: '',
  dosage_type: '',
  schedule: '',
  batch_no: '',
  expiry_date: '',
  available_quantity: '',
  mrp_per_quantity: '',
  reorder_threshold: '',
  sales_discount_for_bill: '',
  grouping: '',
  physical_box_no: '',
  expiry_alert_period: '',
  hsn_code: '',
  drug_ms_id: null,
  reasonCode: 0,
  correctionComments: ''
});

function PharmaInventoryAuditLogEditModal({
  open,
  initialRow,
  onClose,
  onSuccess,
  onError,
  editAccess
}) {
  const { t } = useTranslation();
  const [editForm, setEditForm] = useState(emptyForm);
  const [editSnapshot, setEditSnapshot] = useState(null);
  const [openExpire, setOpenExpire] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [genericNameList, setGenericNameList] = useState([]);
  const [dosageTypeList, setDosageTypeList] = useState([]);
  const [allowForceUpdate, setAllowForceUpdate] = useState(false);
  const [isWarningMsg, setIsWarningMsg] = useState(false);
  const [isWarningMsgText, setIsWarningMsgText] = useState('');
  const prevOpenRef = useRef(false);

  const msgCloseWarning = useCallback(() => {
    setIsWarningMsg(false);
    setIsWarningMsgText('');
  }, []);

  const loadMasterDropdowns = useCallback(() => {
    try {
      RestAPIService.getAll(Serviceurls.PHARMA_DOSAGE_TYPE_LIST)
        .then((response) => {
          if ((response.data?.status || '').toLowerCase() === 'success') {
            const raw = response.data?.data || [];
            const labels = raw
              .map(dosageTypeToString)
              .filter((label) => label && label !== 'N/A');
            const list = ['N/A', ...labels];
            setDosageTypeList(list);
          }
        })
        .catch(() => {});
      RestAPIService.getAll(Serviceurls.PHARMA_GENERIC_NAME_LIST)
        .then((response) => {
          if ((response.data?.status || '').toLowerCase() === 'success') {
            setGenericNameList(response.data?.data || []);
          }
        })
        .catch(() => {});
    } catch (e) {
      /* ignore */
    }
  }, []);

  const bootstrapFromRow = useCallback((row) => {
    if (!row) return;
    const next = {
      stock_id: row.stock_id,
      drug_name: row.drug_name ?? '',
      generic_name: row.generic_name || '',
      generic_id: row.generic_id ?? row.generic_ms_id ?? null,
      drug_strength: row.strength ?? row.dosage_strength ?? '',
      dosage_type: dosageTypeToString(row.dosage_type) || '',
      schedule: row.schedule ?? row.schedules ?? '',
      batch_no: row.batch_no ?? '',
      expiry_date: row.expiry_date ?? '',
      available_quantity:
        row.available_quantity !== undefined && row.available_quantity !== null
          ? String(row.available_quantity)
          : '',
      mrp_per_quantity:
        row.mrp_per_quantity !== undefined && row.mrp_per_quantity !== null
          ? String(row.mrp_per_quantity)
          : '',
      reorder_threshold:
        row.reorder_threshold !== undefined && row.reorder_threshold !== null
          ? String(row.reorder_threshold)
          : '',
      sales_discount_for_bill:
        row.sales_discount_for_bill !== undefined &&
        row.sales_discount_for_bill !== null
          ? String(row.sales_discount_for_bill)
          : '',
      grouping: row.grouping ?? row.group ?? '',
      physical_box_no: row.physical_box_no ?? row.physical_box_number ?? '',
      expiry_alert_period: (() => {
        const v = row.expiry_alert_period;
        if (v === '' || v == null) return '';
        const n = Number(v);
        return [1, 2, 3, 6].includes(n) ? n : '';
      })(),
      hsn_code: row.hsn_code != null ? String(row.hsn_code) : '',
      drug_ms_id: row.drug_ms_id ?? row.drug_id ?? null,
      reasonCode: 0,
      correctionComments: ''
    };
    setEditForm(next);
    setEditSnapshot(JSON.stringify({ editForm: next }));
    setOpenExpire(false);
    setAllowForceUpdate(false);
    setIsWarningMsg(false);
    setIsWarningMsgText('');
  }, []);

  useEffect(() => {
    if (!open) {
      setEditForm(emptyForm());
      setEditSnapshot(null);
      setOpenExpire(false);
      setSaveLoading(false);
      setAllowForceUpdate(false);
      setIsWarningMsg(false);
      setIsWarningMsgText('');
      prevOpenRef.current = false;
      return;
    }
    if (!prevOpenRef.current) {
      loadMasterDropdowns();
    }
    prevOpenRef.current = true;
  }, [open, loadMasterDropdowns]);

  useEffect(() => {
    if (open && initialRow) {
      bootstrapFromRow(initialRow);
    }
  }, [open, initialRow, bootstrapFromRow]);

  const onFieldChange = (e) => {
    const { name, value } = e.target;
    const isNum = CommonValidation.numberOnly(value);
    const isDeci = CommonValidation.DecimalNumber(value);
    setEditForm((prev) => {
      const next = { ...prev };
      if (name === 'available_quantity' || name === 'reorder_threshold') {
        if ((isNum || value === '') && Number(value) < 1000000) {
          next[name] = value;
        }
      } else if (name === 'mrp_per_quantity') {
        if (value === '' || isDeci) {
          next[name] = value;
        }
      } else if (name === 'sales_discount_for_bill') {
        if (value === '' || (isDeci && Number(value) <= 100)) {
          next[name] = value;
        }
      } else if (name === 'schedule') {
        if (value.length <= 100) {
          next[name] = value;
        }
      } else if (
        name === 'drug_name' ||
        name === 'grouping' ||
        name === 'physical_box_no' ||
        name === 'hsn_code'
      ) {
        next[name] = value;
      } else if (name === 'expiry_alert_period') {
        next[name] = value === '' ? '' : Number(value);
      } else {
        next[name] = value;
      }
      return next;
    });
  };

  const adjustQty = (delta) => {
    setEditForm((prev) => {
      const q = Number(prev.available_quantity);
      const base = Number.isNaN(q) ? 0 : q;
      const n = Math.max(0, base + delta);
      if (n >= 1000000) return prev;
      return { ...prev, available_quantity: String(n) };
    });
  };

  const resetForm = () => {
    if (!editSnapshot) return;
    try {
      const parsed = JSON.parse(editSnapshot);
      setEditForm({ ...parsed.editForm });
      setAllowForceUpdate(false);
      setIsWarningMsg(false);
      setIsWarningMsgText('');
    } catch (e) {
      /* ignore */
    }
  };

  const handleSave = () => {
    if (!editAccess) return;
    if (!editForm.stock_id) {
      onError(t('ManitoryFields'));
      return;
    }
    if (!editForm.reasonCode || editForm.reasonCode === 0) {
      onError(t('ManitoryFields'));
      return;
    }
    if (!String(editForm.correctionComments || '').trim()) {
      onError(t('ManitoryFields'));
      return;
    }
    if (!String(editForm.generic_name || '').trim() || !String(editForm.dosage_type || '').trim()) {
      onError(t('ManitoryFields'));
      return;
    }
    const qty = Number(editForm.available_quantity);
    const mrp = Number(editForm.mrp_per_quantity);
    const reorder = Number(editForm.reorder_threshold);
    const discRaw =
      editForm.sales_discount_for_bill === '' || editForm.sales_discount_for_bill == null
        ? 0
        : Number(editForm.sales_discount_for_bill);
    if (Number.isNaN(qty) || Number.isNaN(mrp) || Number.isNaN(reorder)) {
      onError(t('ManitoryFields'));
      return;
    }
    if (Number.isNaN(discRaw) || discRaw < 0 || discRaw > 100) {
      onError(t('ManitoryFields'));
      return;
    }
    const expiryOut =
      getLastDateOfMonth(editForm.expiry_date) || editForm.expiry_date || null;
    const correctionText = String(editForm.correctionComments || '').trim();
    const body = {
      stock_id: editForm.stock_id,
      drug_name: editForm.drug_name?.trim(),
      generic_name: String(editForm.generic_name || '').trim(),
      generic_id: editForm.generic_id,
      drug_strength: editForm.drug_strength,
      dosage_type: editForm.dosage_type,
      schedule: editForm.schedule,
      batch_no: CommonValidation.removeSpace(editForm.batch_no || ''),
      expiry_date: expiryOut,
      available_quantity: qty,
      mrp_per_quantity: mrp,
      reorder_threshold: reorder,
      sales_discount_for_bill: discRaw,
      grouping: editForm.grouping,
      physical_box_no: editForm.physical_box_no,
      expiry_alert_period: editForm.expiry_alert_period === '' ? null : editForm.expiry_alert_period,
      hsn_code: editForm.hsn_code,
      reason: correctionText,
      comments: correctionText,
      force_update: allowForceUpdate
    };
    if (editForm.drug_ms_id != null && editForm.drug_ms_id !== '') {
      body.drug_ms_id = editForm.drug_ms_id;
    }
    setSaveLoading(true);
    RestAPIService.updateWithOutId(body, Serviceurls.PHARMA_INVENTORY_AUDIT_LOG)
      .then((response) => {
        const st = (response.data?.status || '').toLowerCase();
        if (st === 'success') {
          setSaveLoading(false);
          setAllowForceUpdate(false);
          setIsWarningMsg(false);
          setIsWarningMsgText('');
          onSuccess(response.data?.message || t('Record Saved Successfully'));
          onClose();
        } else {
          setSaveLoading(false);
          onError(response.data?.message || t('Failed'));
        }
      })
      .catch((error) => {
        setSaveLoading(false);
        const failStatus = String(error?.response?.data?.status || '').toLowerCase();
        const apiMsg = error?.response?.data?.message;
        if (failStatus === 'fail' && apiMsg === 'Drug already exists') {
          setAllowForceUpdate(true);
          setIsWarningMsg(true);
          setIsWarningMsgText(t('UpdateExistDrug'));
          return;
        }
        if (apiMsg) {
          onError(apiMsg);
        } else {
          onError(error?.message || t('Failed'));
        }
      });
  };

  let userData = {};
  try {
    userData = JSON.parse(localGetItem('loggedInUserInfo') || '{}');
  } catch (e) {
    userData = {};
  }
  const allowBatchExpiry = !!userData?.enable_mrp_edit_option;
  const minExpiryMonth = new Date();
  minExpiryMonth.setMonth(minExpiryMonth.getMonth() + 1);
  minExpiryMonth.setDate(1);

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        overflow: 'auto'
      }}
    >
      <div
        className="eMed_stock_edit_popup"
        style={{
          position: 'relative',
          top: 'auto',
          left: 'auto',
          transform: 'none',
          margin: 'auto',
          maxHeight: 'calc(100vh - 48px)',
          overflow: 'auto',
          minHeight: 'auto',
          width: '65vw'
        }}
      >
        <div className="stock_edit_header">
          <label className="stock_edit_header_text">
            {t('Inventory')} — Master correction — {t('Edit')}
          </label>
          <div className="stock_edit_close" emed_tid="inv_mc_esc_close">
            <img
              className="stock_edit_img_close"
              src={ImagePaths.LabCloseButton.default}
              onClick={onClose}
              alt="close"
            />
          </div>
        </div>
        <div
          className="eMed_stock_edit_middle_card"
          style={{
            maxHeight: '42vh',
            overflowY: 'auto',
            minHeight: 'auto',
            margin: '0.625vw'
          }}
        >
          <div className="eMed_active_view_textfield">
            <TextField
              size="small"
              sx={{ marginRight: '1vw', width: '14.2vw' }}
              label={`${t('BrandName')} *`}
              name="drug_name"
              value={editForm.drug_name}
              onChange={onFieldChange}
              inputProps={{ emed_tid: 'inv_mc_drug_name', maxLength: 200 }}
            />
            <FormControl>
              <Autocomplete
                clearIcon={false}
                disableClearable
                value={
                  genericNameList.find(
                    (item) => item?.generic_name === editForm.generic_name
                  ) || null
                }
                options={genericNameList}
                getOptionLabel={(item) => item?.generic_name || ''}
                sx={{ marginRight: '1vw', width: '14.2vw' }}
                onChange={(e, newValue) => {
                  setEditForm((prev) => ({
                    ...prev,
                    generic_id: newValue?.id ?? null,
                    generic_name: newValue?.generic_name || ''
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label={`${t('GenericName')} *`}
                    inputProps={{
                      ...params.inputProps,
                      type: 'search',
                      emed_tid: 'inv_mc_generic_name'
                    }}
                  />
                )}
              />
            </FormControl>
            <TextField
              size="small"
              sx={{ marginRight: '1vw', width: '14.2vw' }}
              label={`${t('Strength')} *`}
              name="drug_strength"
              value={editForm.drug_strength}
              onChange={onFieldChange}
              inputProps={{ emed_tid: 'inv_mc_strength', maxLength: 100 }}
            />
            <FormControl>
              <Autocomplete
                disableClearable
                value={editForm.dosage_type || null}
                options={dosageTypeList}
                getOptionLabel={(opt) => (typeof opt === 'string' ? opt : dosageTypeToString(opt))}
                isOptionEqualToValue={(opt, val) =>
                  dosageTypeToString(opt) === dosageTypeToString(val)
                }
                sx={{ marginRight: '1vw', width: '14.2vw' }}
                onChange={(e, newValue) => {
                  setEditForm((prev) => ({
                    ...prev,
                    dosage_type: dosageTypeToString(newValue) || ''
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label={`${t('DosageType')} *`}
                    inputProps={{
                      ...params.inputProps,
                      type: 'search',
                      emed_tid: 'inv_mc_dosage_type'
                    }}
                  />
                )}
              />
            </FormControl>
          </div>
          <div className="eMed_active_view_textfield">
            <TextField
              disabled={!allowBatchExpiry}
              size="small"
              sx={{ marginRight: '1vw', width: '14.2vw' }}
              label={`${t('BatchNo')} *`}
              name="batch_no"
              value={editForm.batch_no}
              onChange={onFieldChange}
              inputProps={{ emed_tid: 'inv_mc_batch', maxLength: 50 }}
            />
            <TextField
              size="small"
              sx={{ marginRight: '1vw', width: '14.2vw' }}
              label={t('Schedule')}
              name="schedule"
              value={editForm.schedule}
              onChange={onFieldChange}
              inputProps={{ emed_tid: 'inv_mc_sched', maxLength: 100 }}
            />
            <TextField
              size="small"
              sx={{ marginRight: '1vw', width: '14.2vw' }}
              label={t('Grouping')}
              name="grouping"
              value={editForm.grouping}
              onChange={onFieldChange}
              inputProps={{ emed_tid: 'inv_mc_grouping', maxLength: 200 }}
            />
            <TextField
              size="small"
              sx={{ marginRight: '1vw', width: '14.2vw' }}
              label={t('ReorderThreshold')}
              InputProps={{
                endAdornment: <InputAdornment position="start">Nos</InputAdornment>
              }}
              name="reorder_threshold"
              value={editForm.reorder_threshold}
              onChange={onFieldChange}
              inputProps={{ emed_tid: 'inv_mc_reorder' }}
            />
          </div>
          <div className="eMed_active_view_textfield">
            <TextField
              size="small"
              sx={{ marginRight: '1vw', width: '14.2vw' }}
              label={t('PhyBoxNo')}
              name="physical_box_no"
              value={editForm.physical_box_no}
              onChange={onFieldChange}
              inputProps={{ emed_tid: 'inv_mc_physical_box', maxLength: 100 }}
            />
            <FormControl size="small" sx={{ marginRight: '1vw', width: '14.2vw' }}>
              <InputLabel>{t('ExpiryPeriod')}</InputLabel>
              <Select
                label={t('ExpiryPeriod')}
                name="expiry_alert_period"
                displayEmpty
                value={
                  editForm.expiry_alert_period === '' || editForm.expiry_alert_period == null
                    ? ''
                    : editForm.expiry_alert_period
                }
                onChange={onFieldChange}
              >
                <MenuItem value="">
                  <em>—</em>
                </MenuItem>
                {ExpiryAlertList.map((list) => (
                  <MenuItem key={list.value} value={list.value} emed_tid="inv_mc_exp_alert">
                    {list.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              sx={{ marginRight: '1vw', width: '14.2vw', textAlign: 'center' }}
              label={t('Quantity')}
              name="available_quantity"
              value={editForm.available_quantity}
              onChange={onFieldChange}
              inputProps={{
                style: { textAlign: 'center' },
                emed_tid: 'inv_mc_qty'
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton emed_tid="inv_mc_qty_minus" onClick={() => adjustQty(-1)}>
                      <RemoveIcon color="primary" />
                    </IconButton>
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton emed_tid="inv_mc_qty_add" onClick={() => adjustQty(1)}>
                      <AddIcon color="primary" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                open={openExpire}
                disabled={!allowBatchExpiry}
                onOpen={() => setOpenExpire(true)}
                onClose={() => setOpenExpire(false)}
                minDate={minExpiryMonth}
                views={['year', 'month']}
                inputFormat="MM-yyyy"
                label={t('ExpiryDate')}
                value={
                  editForm.expiry_date
                    ? formatExpiryDateToDateValue(editForm.expiry_date)
                    : null
                }
                onChange={(value) => {
                  if (!value) {
                    setEditForm((prev) => ({ ...prev, expiry_date: '' }));
                    setOpenExpire(false);
                    return;
                  }
                  setEditForm((prev) => ({
                    ...prev,
                    expiry_date: DateTime.fromJSDate(value).toFormat('yyyy-MM-dd')
                  }));
                  setOpenExpire(false);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    onKeyDown={(e) => e.preventDefault()}
                    autoComplete="off"
                    size="small"
                    sx={{ marginRight: '1vw', width: '14.2vw' }}
                    inputProps={{
                      ...params.inputProps,
                      emed_tid: 'inv_mc_exp_date'
                    }}
                    onClick={() => setOpenExpire(true)}
                  />
                )}
              />
            </LocalizationProvider>
          </div>
          <div
            className="eMed_active_view_textfield"
            style={{ display: 'flex', flexDirection: 'row', width: '100%' }}
          >
            <TextField
              size="small"
              sx={{ width: '14.2vw', marginLeft: '1vw' }}
              label={t('SalesDiscount%')}
              name="sales_discount_for_bill"
              value={editForm.sales_discount_for_bill}
              onChange={onFieldChange}
              inputProps={{ emed_tid: 'inv_mc_sales_discount_for_bill', step: '0.01' }}
            />
            {allowBatchExpiry ? (
              <TextField
                size="small"
                sx={{ width: '14.2vw', marginLeft: '1vw' }}
                label={t(`MRP/Qty (${CurrencySymbol})`)}
                name="mrp_per_quantity"
                value={editForm.mrp_per_quantity}
                onChange={onFieldChange}
                inputProps={{ emed_tid: 'inv_mc_mrp', step: '0.01' }}
              />
            ) : null}
            <TextField
              size="small"
              sx={{ width: '14.2vw', marginLeft: '1vw' }}
              label={t('HSNCode')}
              name="hsn_code"
              value={editForm.hsn_code}
              onChange={onFieldChange}
              inputProps={{ emed_tid: 'inv_mc_hsn', maxLength: 20 }}
            />
          </div>
        </div>
        <div style={{ minHeight: '10vw' }}>
          <div className="eMed_stock_edit_radio_card" style={{ boxShadow: 'none' }}>
            <label className="stock_edit_radio_text">
              {'Select the Reason for Correction *'}
            </label>
            <div className="stock_edit_radio">
              <FormControl>
                <RadioGroup
                  row
                  name="inv_mc_reason_radio"
                  value={editForm.reasonCode}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setEditForm((prev) => ({ ...prev, reasonCode: v }));
                  }}
                >
                  <FormControlLabel
                    value={1}
                    control={<Radio emed_tid="inv_mc_Breakage" />}
                    label={t('Breakage')}
                  />
                  <FormControlLabel
                    value={2}
                    control={<Radio emed_tid="inv_mc_Loss" />}
                    label={t('Loss')}
                  />
                  <FormControlLabel
                    value={3}
                    control={<Radio emed_tid="inv_mc_Expired" />}
                    label={t('Expired')}
                  />
                  <FormControlLabel
                    value={4}
                    control={<Radio emed_tid="inv_mc_Miscellaneous" />}
                    label={t('Miscellaneous')}
                  />
                  <FormControlLabel
                    value={5}
                    control={<Radio emed_tid="inv_mc_Others" />}
                    label={t('Others')}
                  />
                </RadioGroup>
              </FormControl>
            </div>
          </div>
          <div className="eMed_stock_edit_input_card" style={{ boxShadow: 'none' }}>
            <TextField
              sx={{ width: '61vw', marginLeft: '1vw' }}
              multiline
              rows={2}
              maxRows={2}
              label={`${t('correction')} *`}
              name="correctionComments"
              value={editForm.correctionComments}
              onChange={onFieldChange}
              inputProps={{ emed_tid: 'inv_mc_comments', maxLength: 200 }}
            />
          </div>
          <div className="eMed_active_view_footer" style={{ margin: '1vw' }}>
            <Button
              emed_tid="inv_mc_reset"
              sx={{ marginRight: '0.5vw', textTransform: 'capitalize' }}
              variant="outlined"
              onClick={resetForm}
            >
              {t('Reset')}
            </Button>
            <Button
              emed_tid="inv_mc_save"
              disabled={
                saveLoading ||
                !editAccess ||
                !editForm.reasonCode ||
                !String(editForm.correctionComments || '').trim() ||
                !editForm.drug_name ||
                !editForm.batch_no ||
                !String(editForm.generic_name || '').trim() ||
                !String(editForm.dosage_type || '').trim()
              }
              sx={{ textTransform: 'capitalize' }}
              variant="contained"
              onClick={handleSave}
            >
              {t('Save')}
            </Button>
          </div>
        </div>
        {isWarningMsg ? (
          <ToastMsg
            severity="warning"
            msg={isWarningMsgText}
            msgPop={msgCloseWarning}
          />
        ) : null}
      </div>
    </Modal>
  );
}

export default PharmaInventoryAuditLogEditModal;
